/**
 * Theme state for the design prototype.
 *
 * Deliberately NOT wired into Redux: the prototype has to stay self-contained
 * so it can be deleted (or promoted) in one move. Preferences persist to
 * localStorage and fall back to the OS colour-scheme on first visit.
 *
 * `ThemeProvider` owns the state; `DsRoot` renders the scoping element that
 * actually carries the `data-ds-*` attributes. They are separate on purpose —
 * the compare view needs five independently themed roots on one page, which a
 * provider-renders-the-div design could not express.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  HTMLAttributes,
} from 'react';
import { Density, Mode, ThemeId, getTheme, isThemeId } from './themes';
import { FEATURE_FLAGS } from '../config/featureFlags';

const DEFAULT_THEME: ThemeId = 'saga-gold';

type ThemeState = {
  theme: ThemeId;
  mode: Mode;
  density: Density;
  /** User-forced reduced motion, on top of the OS-level media query. */
  reduceMotion: boolean;
  setTheme: (t: ThemeId) => void;
  setMode: (m: Mode) => void;
  setDensity: (d: Density) => void;
  setReduceMotion: (v: boolean) => void;
  /**
   * Shell sidebar state. `null` means the user has never chosen, so the
   * viewport decides; `true`/`false` is an explicit choice that wins at
   * >=900px. It lives here rather than in its own storage key because this
   * provider persists with a wholesale `JSON.stringify`, so a second writer
   * on the same key would be clobbered on the next theme change.
   */
  sidebarCollapsed: boolean | null;
  setSidebarCollapsed: (v: boolean | null) => void;
  toggleMode: () => void;
};

const STORAGE_KEY = 'maintener.design.prefs';

const ThemeContext = createContext<ThemeState | null>(null);

type StoredPrefs = Partial<{
  theme: ThemeId;
  mode: Mode;
  density: Density;
  reduceMotion: boolean;
  sidebarCollapsed: boolean | null;
}>;

const readPrefs = (): StoredPrefs => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredPrefs) : {};
  } catch {
    // Private mode / disabled storage — fall back to defaults rather than crash.
    return {};
  }
};

const prefersDark = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

type ThemeProviderProps = {
  children: ReactNode;
  /**
   * Called when the user picks a direction (not on load, and not when the mode
   * or density change). The demo routes use it to keep the address bar
   * truthful.
   *
   * This is a notification, not a sync: an effect that watched `theme` and
   * navigated after the fact raced with the effect that adopts the theme from
   * the URL — and `StrictMode`'s double-invoked effects made the race
   * reproducible, so opening /design/tactile would land on whatever was in
   * localStorage. One-directional notification has no such failure mode.
   */
  onThemeChange?: (id: ThemeId) => void;
  /**
   * Write the theme attributes onto the `#root` element, theming the whole
   * production app. Set by the app-level provider in `src/index.tsx` only —
   * the `/design` prototype drives its own `.ds-root` elements instead, so it
   * leaves this off and two providers never fight over the same element.
   */
  applyToRoot?: boolean;
};

export const ThemeProvider = ({
  children,
  onThemeChange,
  applyToRoot = false,
}: ThemeProviderProps) => {
  const [prefs] = useState(readPrefs);

  const [theme, setTheme] = useState<ThemeId>(
    prefs.theme && isThemeId(prefs.theme) ? prefs.theme : DEFAULT_THEME,
  );
  const [mode, setMode] = useState<Mode>(
    prefs.mode ?? (prefersDark() ? 'dark' : 'light'),
  );
  const [density, setDensity] = useState<Density>(prefs.density ?? 'compact');
  const [reduceMotion, setReduceMotion] = useState<boolean>(
    prefs.reduceMotion ?? false,
  );
  // Objects stored by an older bundle have no such key, and `?? null` gives
  // exactly the fresh-install value - so no migration is needed.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean | null>(
    prefs.sidebarCollapsed ?? null,
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ theme, mode, density, reduceMotion, sidebarCollapsed }),
      );
    } catch {
      // Persistence is a nicety, not a requirement.
    }
  }, [theme, mode, density, reduceMotion, sidebarCollapsed]);

  /**
   * What the production app actually applies. While a feature flag hides the
   * control for a preference, that preference is pinned to its default -
   * otherwise a stored choice (or an OS dark-mode default) would stick with no
   * UI to undo it. The raw state above is still persisted, so re-enabling the
   * flag restores the user's choices. The `/design` prototype (no
   * `applyToRoot`) is never locked.
   */
  const lockSettings = applyToRoot && !FEATURE_FLAGS.displaySettings;
  const lockMode = lockSettings && !FEATURE_FLAGS.darkModeToggle;
  const effectiveTheme = lockSettings ? DEFAULT_THEME : theme;
  const effectiveMode = lockMode ? getTheme(DEFAULT_THEME)?.defaultMode ?? 'light' : mode;
  const effectiveDensity = lockSettings ? 'compact' : density;
  const effectiveReduceMotion = lockSettings ? false : reduceMotion;

  /**
   * Theme the real app by putting the attributes on `#root` itself. The token
   * files declare on `#root[data-ds-theme=…]` as well as `.ds-root[…]`, and
   * `tailwind.config.js` resolves its colours from those variables — so this
   * one effect restyles every Tailwind colour utility in the codebase.
   */
  useEffect(() => {
    if (!applyToRoot) return;
    const root = document.getElementById('root');
    if (!root) return;
    root.setAttribute('data-ds-theme', effectiveTheme);
    root.setAttribute('data-ds-mode', effectiveMode);
    root.setAttribute('data-ds-density', effectiveDensity);
    if (effectiveReduceMotion) root.setAttribute('data-ds-reduce-motion', 'true');
    else root.removeAttribute('data-ds-reduce-motion');
  }, [applyToRoot, effectiveTheme, effectiveMode, effectiveDensity, effectiveReduceMotion]);

  /**
   * Switching theme also switches to that theme's intended mode — Aurora Noir
   * is designed dark-first, and landing on its light mode first would
   * misrepresent it. Only applies when the user picks a theme, never on reload.
   */
  const selectTheme = useCallback(
    (next: ThemeId) => {
      setTheme(next);
      const meta = getTheme(next);
      if (meta) setMode(meta.defaultMode);
      onThemeChange?.(next);
    },
    [onThemeChange],
  );

  const toggleMode = useCallback(
    () => setMode((m) => (m === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo<ThemeState>(
    () => ({
      theme: effectiveTheme,
      mode: effectiveMode,
      density: effectiveDensity,
      reduceMotion: effectiveReduceMotion,
      setTheme: selectTheme,
      setMode,
      setDensity,
      setReduceMotion,
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleMode,
    }),
    [
      effectiveTheme,
      effectiveMode,
      effectiveDensity,
      effectiveReduceMotion,
      sidebarCollapsed,
      selectTheme,
      toggleMode,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useDsTheme = (): ThemeState => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useDsTheme must be used inside <ThemeProvider>');
  return ctx;
};

/** The theme actually in force on the nearest `DsRoot`, overrides included. */
export type ResolvedTheme = {
  theme: ThemeId;
  mode: Mode;
  density: Density;
  reduceMotion: boolean;
};

const ResolvedThemeContext = createContext<ResolvedTheme | null>(null);

/**
 * The values the nearest `DsRoot` resolved to, falling back to the ambient
 * `ThemeProvider` and then to the defaults.
 *
 * Needed because `DsRoot` accepts explicit overrides: the compare view renders
 * five roots that each force their own theme, and anything reading the ambient
 * provider directly would report the wrong one.
 */
export const useResolvedTheme = (): ResolvedTheme => {
  const scoped = useContext(ResolvedThemeContext);
  const ambient = useContext(ThemeContext);

  return (
    scoped ?? {
      theme: ambient?.theme ?? DEFAULT_THEME,
      mode: ambient?.mode ?? 'light',
      density: ambient?.density ?? 'compact',
      reduceMotion: ambient?.reduceMotion ?? false,
    }
  );
};

type DsRootProps = {
  children: ReactNode;
  /** Override the ambient theme — used by the side-by-side compare view. */
  theme?: ThemeId;
  mode?: Mode;
  density?: Density;
} & HTMLAttributes<HTMLDivElement>;

/**
 * The scoping element. Every `--ds-*` variable is declared on `.ds-root`, so
 * nothing here can leak into the production app, and multiple roots with
 * different themes can coexist on one page.
 */
export const DsRoot = ({
  children,
  theme,
  mode,
  density,
  className = '',
  ...rest
}: DsRootProps) => {
  const ctx = useContext(ThemeContext);

  const resolved = useMemo<ResolvedTheme>(
    () => ({
      theme: theme ?? ctx?.theme ?? DEFAULT_THEME,
      mode: mode ?? ctx?.mode ?? 'light',
      density: density ?? ctx?.density ?? 'compact',
      reduceMotion: ctx?.reduceMotion ?? false,
    }),
    [theme, mode, density, ctx?.theme, ctx?.mode, ctx?.density, ctx?.reduceMotion],
  );

  return (
    <div
      className={`ds-root ${className}`}
      data-ds-theme={resolved.theme}
      data-ds-mode={resolved.mode}
      data-ds-density={resolved.density}
      data-ds-reduce-motion={resolved.reduceMotion ? 'true' : undefined}
      {...rest}
    >
      {/* Published so portaled overlays can re-create this scope outside the
          DOM subtree — see OverlayLayer. A compare-view poster overrides the
          ambient theme, and its overlays must follow the override, not the
          ambient value. */}
      <ResolvedThemeContext.Provider value={resolved}>
        {children}
      </ResolvedThemeContext.Provider>
    </div>
  );
};
